/**
 * Warmup (daily games) palette + Football Wordle layout constants.
 *
 * The Warmup surface is a distinct dark-purple game skin (from the provided design) that sits on top of
 * the app. Its colors live here as named tokens so styles never hardcode hex inline. Values mirror
 * `Warmup Soccer Wordle.dc.html`.
 */

export const WORDLE_GAME_KEY = 'WORDLE';

/** Display-only reward figures (server is the source of truth for what's actually awarded). */
export const WORDLE_WIN_REWARD_COINS = 50;
export const WORDLE_STREAK_BONUS_COINS = 100;
export const WORDLE_STREAK_CYCLE = 7;

export const WARMUP_BANNER_PATH = 'assets/backgrounds/Warmup.webp';

/** R2 card artwork (zporti-assets bucket) for the hub game cards' image banner. */
export const WARMUP_CARD_IMAGES = {
  wordle: 'assets/warmup/Wordle1.webp',
  fans: 'assets/warmup/Fan_Favorite.webp',
  hilo: 'assets/warmup/Higher_Lower.webp',
  footballer: 'assets/warmup/Football_quiz.webp',
} as const;

export const WARMUP_COLORS = {
  bg: '#0F0A1A',
  surface: '#1A1326',
  surfaceAlt: '#1F2937',
  card: '#161021',
  cardRaised: '#26203A',
  border: '#2A2F3A',
  borderPurple: '#3A2F57',
  borderPurpleStrong: '#6D4FC0',
  purple: '#A78BFA',
  purpleDeep: '#5B33B5',
  green: '#6EE7B7',
  amber: '#FCD34D',
  amberBorder: '#4D3C12',
  amberSurface: '#2A2310',
  absent: '#374151',
  keyBg: '#2A2F3A',
  keyActionBg: '#4C3A6B',
  text: '#F7FAFC',
  textDim: '#9CA3AF',
  textFaint: '#6B7280',
  textOnBright: '#0F1720',
  danger: '#F87171',
} as const;

/**
 * Warmup hub game-card palette (from the handoff design). The Wordle card is a live purple card; the
 * Fan Favourites card is shown muted with a "SOON" badge until that game ships.
 */
export const WARMUP_HUB = {
  // Image-banner card body (design "#0f0a18") behind the artwork / icon area.
  cardArtBg: '#0F0A18',
  // Per-game accent — the card's 1.5px border + glow (design `accent`).
  wordleAccent: '#A78BFA',
  hiloAccent: '#6EE7B7',
  fansAccent: '#FCD34D',
  // Image-area gradient for artless ("coming soon") cards, behind the centered icon.
  wordleCardGradient: ['#26203A', '#1C1729'] as [string, string],
  top10CardGradient: ['#2D2616', '#211D12'] as [string, string],
  top10Accent: '#FCD34D',
  valueCardGradient: ['#16271F', '#11201A'] as [string, string],
  valueAccent: '#6EE7B7',
  trumpsCardGradient: ['#26203A', '#1C1729'] as [string, string],
  trumpsAccent: '#A78BFA',
  buildCardGradient: ['#2C2046', '#1D1730'] as [string, string],
  buildAccent: '#C084FC',
  // Wall Breaker (slingshot) — live action card; warm orange accent over a wood-crate gradient.
  slingshotAccent: '#FB923C',
  slingshotCardGradient: ['#2E1B12', '#21140D'] as [string, string],
  footballerCardGradient: ['#241B3D', '#191327'] as [string, string],
  footballerAccent: '#A78BFA',
  // Keeper Duel — live duel card; sky-blue accent over a night-match gradient.
  keeperDuelAccent: '#38BDF8',
  keeperDuelCardGradient: ['#0E2233', '#0A1826'] as [string, string],
  coinBorder: '#B45309', // darker amber rim on the small reward-coin glyph
} as const;

/** Tile state including the two non-mark visual states the board renders. */
export type WordleTileState = WordleMark | 'filled' | 'empty';

/**
 * Chip-style board tile palette (from the design's `boardStyle: 'chips'` default).
 * Each tile is a vertical gradient face (`top` → `mid`) sitting on a 3px solid `ledge`
 * that gives the raised "chip" edge. `border` is only drawn for the empty/filled states.
 */
export const BOARD_TILE_LEDGE = 3;

export const WORDLE_CHIP_COLORS: Record<
  WordleTileState,
  { top: string; mid: string; ledge: string; text: string; border: string | undefined }
> = {
  empty: { top: '#221A38', mid: '#19122B', ledge: '#100C1B', text: WARMUP_COLORS.text, border: '#322855' },
  filled: { top: '#2A2340', mid: '#1F1930', ledge: '#13101D', text: WARMUP_COLORS.text, border: WARMUP_COLORS.borderPurpleStrong },
  correct: { top: '#86F0C8', mid: '#54D8A4', ledge: '#2A9D77', text: WARMUP_COLORS.textOnBright, border: undefined },
  present: { top: '#FFE27A', mid: '#F5B531', ledge: '#C5860F', text: WARMUP_COLORS.textOnBright, border: undefined },
  absent: { top: '#3B4250', mid: '#2B323D', ledge: '#191F28', text: WARMUP_COLORS.textDim, border: undefined },
};

/**
 * On-screen keyboard chip colors: a flat face on a 3px `ledge`, matching the board chips.
 * `default` is an unused letter, `action` is ENTER/DEL, and correct/present/absent reflect
 * the best mark a letter has earned so far.
 */
export const KEY_CHIP_LEDGE = 3;

export const WORDLE_KEY_COLORS = {
  default: { bg: WARMUP_COLORS.keyBg, text: '#E5E7EB', ledge: '#15111F' },
  action: { bg: WARMUP_COLORS.keyActionBg, text: WARMUP_COLORS.text, ledge: '#2A1F45' },
  correct: { bg: WARMUP_COLORS.green, text: WARMUP_COLORS.textOnBright, ledge: '#2A9D77' },
  present: { bg: WARMUP_COLORS.amber, text: WARMUP_COLORS.textOnBright, ledge: '#C5860F' },
  absent: { bg: '#1A1426', text: '#5B6472', ledge: '#0C0814' },
} as const;

export const KEY_ENTER = 'ENTER';
export const KEY_DEL = 'DEL';

/** On-screen keyboard layout (QWERTY) with ENTER/DEL on the bottom row. */
export const WORDLE_KEYBOARD_ROWS: string[][] = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  [KEY_ENTER, 'Z', 'X', 'C', 'V', 'B', 'N', 'M', KEY_DEL],
];

export type WordleMark = 'correct' | 'present' | 'absent';
